import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MockPlayer from "../components/MockPlayer";
import { ArrowLeft, Clock, Calendar, User, Star, Ticket, Clapperboard } from "lucide-react";

export default function FilmMiniSite() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [film, setFilm] = useState(null);
  const [access, setAccess] = useState({ access: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    document.body.classList.add("theme-mini");
    return () => document.body.classList.remove("theme-mini");
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/films/${slug}`)
      .then(({ data }) => setFilm(data))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!user || user === false || !slug) return;
    api
      .get(`/library/${slug}/access`)
      .then(({ data }) => setAccess(data))
      .catch(() => setAccess({ access: false }));
  }, [slug, user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gs-screen text-white flex items-center justify-center">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gs-neon animate-flicker">Loading reel…</p>
      </div>
    );
  }
  if (error || !film) {
    return (
      <div className="min-h-screen bg-gs-screen text-white flex flex-col items-center justify-center gap-4">
        <p className="font-display text-4xl">FILM NOT FOUND</p>
        <Link to="/" className="font-mono text-xs uppercase tracking-widest text-gs-neon hover:text-white">
          ← back to the marquee
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="mini-site" className="film-grain min-h-screen bg-gs-screen text-white">
      {/* HEADER */}
      <header className="sticky top-0 z-30 backdrop-blur-2xl bg-gs-screen/70 border-b border-white/10">
        <div className="max-w-none px-4 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" data-testid="back-to-greenscreen" className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70 hover:text-gs-neon inline-flex items-center gap-3">
            <ArrowLeft size={12} />
            <img src="/brand/monogram.png" alt="Green Screen" className="h-8 w-auto" />
            <img src="/brand/wordmark.png" alt="Green Screen Pictures" className="hidden md:block h-5 w-auto opacity-95" />
          </Link>
          <p className="font-display text-2xl tracking-wide">{film.title}</p>
          <Link to="/library" className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70 hover:text-gs-neon">My Library</Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative h-[92vh] w-full overflow-hidden">
        {film.trailer_url ? (
          <video
            data-testid="hero-trailer"
            src={film.trailer_url}
            poster={film.backdrop_url}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <img src={film.backdrop_url} alt={film.title} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-gs-screen via-gs-screen/40 to-gs-screen/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-gs-screen via-transparent to-transparent" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-20">
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-gs-neon animate-flicker mb-6">
            ● Now playing · Green Screen Theater
            {film.run_status?.is_running && (
              <span className="ml-3 text-gs-cream/70">— Week {film.run_status.week_index} of {film.run_status.weeks} · {film.run_status.days_remaining} days left</span>
            )}
          </p>
          <h1 className="font-display tracking-tight leading-[0.85] text-[14vw] md:text-[10vw] text-white">
            {film.title.toUpperCase()}
          </h1>
          <p className="font-serif italic text-2xl md:text-3xl text-white/80 mt-6 max-w-2xl">{film.tagline}</p>
          <div className="font-mono text-xs uppercase tracking-widest text-white/60 mt-8 flex flex-wrap items-center gap-x-6 gap-y-2">
            <span className="inline-flex items-center gap-2"><Calendar size={12} /> {film.year}</span>
            <span className="inline-flex items-center gap-2"><Clock size={12} /> {film.runtime_minutes} min</span>
            {film.filmmaker_slug ? (
              <Link to={`/f/${film.filmmaker_slug}`} className="inline-flex items-center gap-2 hover:text-gs-neon">
                <User size={12} /> {film.director}
              </Link>
            ) : (
              <span className="inline-flex items-center gap-2"><User size={12} /> {film.director}</span>
            )}
            <span className="px-2 py-0.5 border border-white/30">{film.rating}</span>
            <span>{film.genre}</span>
          </div>
        </div>
      </section>

      {/* TICKET BOOTH */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-neon mb-6">Synopsis</p>
          <p className="font-serif text-2xl md:text-3xl leading-[1.4] text-white/90 max-w-2xl text-balance">
            {film.synopsis}
          </p>
        </div>
        <div className="md:col-span-5">
          <div className="border border-white/15 bg-white/[0.02] p-8" data-testid="ticket-booth">
            <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-gs-neon mb-6">Box office</p>
            {access.access ? (
              <div>
                <p className="font-display text-3xl tracking-wide mb-2">YOU'RE IN</p>
                <p className="font-mono text-xs text-white/60 mb-6">
                  {access.mode === "buy"
                    ? "Owned · in your library forever."
                    : `Rental ends ${new Date(access.expires_at).toLocaleString()}`}
                </p>
                <a
                  href="#screen"
                  className="block text-center bg-gs-ember text-gs-ink px-8 py-4 font-mono uppercase tracking-[0.25em] text-xs hover:bg-gs-cream hover:text-gs-ink transition-all shadow-[0_0_40px_rgba(201,169,97,0.25)]"
                  data-testid="enter-theater-btn"
                >
                  Enter the theater
                </a>
              </div>
            ) : !user || user === false ? (
              <div>
                <p className="font-mono text-xs text-white/60 mb-6">Sign in to rent or buy this film.</p>
                <Link
                  to={`/login?redirect=/film/${film.slug}`}
                  data-testid="sign-in-to-rent"
                  className="block text-center bg-gs-ember text-gs-ink px-8 py-4 font-mono uppercase tracking-[0.25em] text-xs hover:bg-gs-cream transition-all shadow-[0_0_40px_rgba(201,169,97,0.25)]"
                >
                  Sign in for tickets
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                <Link
                  to={`/film/${film.slug}/checkout?mode=rent`}
                  data-testid="rent-btn"
                  className="flex items-center justify-between bg-gs-ember text-gs-ink px-6 py-4 font-mono uppercase tracking-[0.25em] text-xs hover:bg-gs-cream transition-all shadow-[0_0_40px_rgba(201,169,97,0.25)]"
                >
                  <span className="inline-flex items-center gap-3"><Clapperboard size={16} /> Rent · 48h</span>
                  <span>${film.rent_price.toFixed(2)}</span>
                </Link>
                <Link
                  to={`/film/${film.slug}/checkout?mode=buy`}
                  data-testid="buy-btn"
                  className="flex items-center justify-between bg-transparent text-gs-cream px-6 py-4 font-mono uppercase tracking-[0.25em] text-xs border border-gs-ember/50 hover:bg-gs-ember/10 hover:border-gs-ember transition-all"
                >
                  <span className="inline-flex items-center gap-3"><Ticket size={16} /> Buy · keep forever</span>
                  <span>${film.buy_price.toFixed(2)}</span>
                </Link>
              </div>
            )}
            <ul className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-6 space-y-1">
              <li>· Mock payment for demo</li>
              <li>· Watch immediately after checkout</li>
              <li>· Only available at seegreenscreen.com</li>
            </ul>
          </div>
        </div>
      </section>

      {/* SCREEN */}
      <section id="screen" className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-neon mb-4">The screen</p>
        <MockPlayer film={film} locked={!access.access} accessMode={access.mode} expiresAt={access.expires_at} />
      </section>

      {/* CAST + DETAILS */}
      <section className="border-y border-white/10 bg-gs-screenSurface">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-neon">Direction</p>
            {film.filmmaker_slug ? (
              <Link to={`/f/${film.filmmaker_slug}`} data-testid="director-link" className="font-display text-4xl mt-3 tracking-wide hover:text-gs-neon transition-colors inline-block">
                {film.director}
              </Link>
            ) : (
              <p className="font-display text-4xl mt-3 tracking-wide">{film.director}</p>
            )}
          </div>
          <div className="md:col-span-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-neon">Starring</p>
            <ul className="mt-3 space-y-2 font-serif text-xl text-white/80">
              {film.cast?.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
          <div className="md:col-span-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-neon">Run time</p>
            <p className="font-display text-4xl mt-3 tracking-wide">{film.runtime_minutes} MIN</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-neon mt-8">Format</p>
            <p className="font-serif text-xl text-white/80 mt-2">Digital · Stereo · 16:9</p>
          </div>
        </div>
      </section>

      {/* QUOTES */}
      {film.quotes?.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 md:px-12 py-24">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-neon mb-8">Press</p>
          <div className="space-y-10">
            {film.quotes.map((q, i) => (
              <div key={`${q.source}-${i}`} data-testid={`press-quote-${i}`} className="border-l-2 border-gs-neon pl-6">
                <p className="font-serif italic text-3xl md:text-4xl text-white/90 leading-[1.3]">"{q.quote}"</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mt-3 inline-flex items-center gap-2">
                  <Star size={10} /> {q.source}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FOOTER STRIP */}
      <footer className="border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-white/50 font-mono text-[10px] uppercase tracking-[0.3em]">
          <p>Green Screen Pictures · seegreenscreen.com</p>
          <p>{film.title} © {film.year} · A theatrical release</p>
        </div>
      </footer>
    </div>
  );
}
